import { createSlice, createAsyncThunk } from '@reduxjs/toolkit'
import apiService from 'services/api'
import { setGlobalLoading, fetchOrganizations, fetchUsers, fetchZones, fetchGroups } from 'store/global'

const SLICE_KEY = 'organizations'

export const createOrganization = createAsyncThunk(
  `${SLICE_KEY}/createOrganization`,
  async ({ organization }, { dispatch, rejectWithValue }) => {
    try {
      dispatch(setGlobalLoading(true))
      const data = await apiService.createOrganization(organization)
      dispatch(setGlobalLoading(false))
      dispatch(fetchOrganizations())
      return data
    } catch (error) {
      dispatch(setGlobalLoading(false))
      return rejectWithValue(error)
    }
  },
)

export const editOrganization = createAsyncThunk(
  `${SLICE_KEY}/editOrganization`,
  async ({ organization }, { dispatch, rejectWithValue }) => {
    try {
      dispatch(setGlobalLoading(true))
      const data = await apiService.editOrganization(organization)
      dispatch(setGlobalLoading(false))
      dispatch(fetchOrganizations())
      return data
    } catch (error) {
      dispatch(setGlobalLoading(false))
      return rejectWithValue(error)
    }
  },
)

export const deleteOrganization = createAsyncThunk(
  `${SLICE_KEY}/deleteOrganization`,
  async ({ organizationId }, { dispatch, rejectWithValue }) => {
    try {
      dispatch(setGlobalLoading(true))
      const data = await apiService.deleteOrganization(organizationId)

      dispatch(setGlobalLoading(false))

      dispatch(fetchOrganizations())

      return data
    } catch (error) {
      dispatch(setGlobalLoading(false))
      return rejectWithValue(error)
    }
  },
)

export const submitUser = createAsyncThunk(
  `${SLICE_KEY}/submitUser`,
  async ({ user }, { dispatch, rejectWithValue }) => {
    try {
      dispatch(setGlobalLoading(true))

      const data = await apiService.submitUser(user)

      dispatch(setGlobalLoading(false))

      dispatch(fetchUsers())

      return data
    } catch (error) {
      dispatch(setGlobalLoading(false))

      return rejectWithValue(error)
    }
  },
)

export const updateUser = createAsyncThunk(`${SLICE_KEY}/updateUser`, async ({ user }, { dispatch }) => {
  dispatch(setGlobalLoading(true))

  const data = await apiService.updateUser(user)

  dispatch(setGlobalLoading(false))

  dispatch(fetchUsers())

  return data
})

export const deleteUser = createAsyncThunk(`${SLICE_KEY}/deleteUser`, async ({ userId }, { dispatch }) => {
  dispatch(setGlobalLoading(true))

  const data = await apiService.deleteUser(userId)

  dispatch(setGlobalLoading(false))

  dispatch(fetchUsers())

  return data
})

export const createZone = createAsyncThunk(`${SLICE_KEY}/createZone`, async ({ zone }, { dispatch }) => {
  dispatch(setGlobalLoading(true))
  const data = await apiService.createZone(zone)

  dispatch(setGlobalLoading(false))

  dispatch(fetchZones())

  return data
})

export const editZone = createAsyncThunk(`${SLICE_KEY}/editZone`, async ({ zone }, { dispatch }) => {
  dispatch(setGlobalLoading(true))
  const data = await apiService.editZone(zone)

  dispatch(setGlobalLoading(false))

  dispatch(fetchZones())

  return data
})

export const deleteZone = createAsyncThunk(`${SLICE_KEY}/deleteZone`, async ({ zoneId }, { dispatch }) => {
  dispatch(setGlobalLoading(true))

  const data = await apiService.deleteZone(zoneId)

  dispatch(setGlobalLoading(false))

  dispatch(fetchZones())

  return data
})

export const createGroup = createAsyncThunk(`${SLICE_KEY}/createGroup`, async ({ group }, { dispatch }) => {
  dispatch(setGlobalLoading(true))
  const data = await apiService.createGroup(group)

  dispatch(setGlobalLoading(false))

  dispatch(fetchGroups())

  return data
})

export const editGroup = createAsyncThunk(`${SLICE_KEY}/editGroup`, async ({ group }, { dispatch }) => {
  dispatch(setGlobalLoading(true))

  const data = await apiService.editGroup(group)

  dispatch(setGlobalLoading(false))

  dispatch(fetchGroups())

  return data
})

export const deleteGroup = createAsyncThunk(`${SLICE_KEY}/deleteGroup`, async ({ groupId }, { dispatch }) => {
  dispatch(setGlobalLoading(true))

  const data = await apiService.deleteGroup(groupId)

  dispatch(setGlobalLoading(false))

  dispatch(fetchGroups())

  return data
})

export const initialState = {
  displayedOrganization: {
    isOpen: false,
  },
  displayedUser: {
    isOpen: false,
  },
  displayedZone: {
    isOpen: false,
  },
  displayedGroup: {
    isOpen: false,
  },
}

const slice = createSlice({
  name: SLICE_KEY,
  initialState,
  reducers: {
    setDisplayedOrganization(state, { payload = {} }) {
      state.displayedOrganization = payload.organization || { isOpen: false }
    },
    updateDisplayedOrganization(state, { payload = {} }) {
      const { key, value } = payload

      state.displayedOrganization = {
        ...state.displayedOrganization,
        [key]: value,
      }
    },
    closeDisplayedOrganization(state) {
      state.displayedOrganization = {
        ...state.displayedOrganization,
        isOpen: false,
      }
    },
    setDisplayedUser(state, { payload = {} }) {
      state.displayedUser = payload.user || { isOpen: false }
    },
    updateDisplayedUser(state, { payload = {} }) {
      const { key, value } = payload

      state.displayedUser = {
        ...state.displayedUser,
        [key]: value,
      }
    },
    closeDisplayedUser(state) {
      state.displayedUser = {
        ...state.displayedUser,
        isOpen: false,
      }
    },
    setDisplayedZone(state, { payload = {} }) {
      state.displayedZone = payload.zone || { isOpen: false }
    },
    updateDisplayedZone(state, { payload = {} }) {
      const { key, value } = payload

      state.displayedZone = {
        ...state.displayedZone,
        [key]: value,
      }
    },
    closeDisplayedZone(state) {
      state.displayedZone = {
        ...state.displayedZone,
        isOpen: false,
      }
    },
    setDisplayedGroup(state, { payload = {} }) {
      state.displayedGroup = payload.group || { isOpen: false }
    },
    updateDisplayedGroup(state, { payload = {} }) {
      const { key, value } = payload

      state.displayedGroup = {
        ...state.displayedGroup,
        [key]: value,
      }
    },
    closeDisplayedGroup(state) {
      state.displayedGroup = {
        ...state.displayedGroup,
        isOpen: false,
      }
    },
  },
})

export const {
  setDisplayedOrganization,
  updateDisplayedOrganization,
  closeDisplayedOrganization,
  setDisplayedUser,
  updateDisplayedUser,
  closeDisplayedUser,
  setDisplayedZone,
  updateDisplayedZone,
  closeDisplayedZone,
  setDisplayedGroup,
  updateDisplayedGroup,
  closeDisplayedGroup,
} = slice.actions

export const organizationsReducer = slice.reducer
export default slice
